import { useAuth } from "@clerk/clerk-expo";
import { cacheKeys } from "@constants/cache-keys";
import { PRODUCT_METADATA_STALE_TIME } from "@constants/stale-times";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import type { ProductType } from "@memorang/types";
import { useQuery } from "@tanstack/react-query";
import {
	fetchProductDetails,
	fetchProductMetadata,
} from "api/commerce/product-service";
import { useAppStore } from "./useAppStore";

export const useProductDetails = (
	productType: ProductType,
	explicitProductNumericId?: number,
	options?: { enabled?: boolean },
) => {
	const {
		currentExamContext: {
			prepNumericId,
			studyPackProductNumericId,
			practiceTestProductNumericId,
		},
		isExamContextLoading,
	} = useExamContext();

	const { isSignedIn } = useAuth();

	const getProductNumericId = () => {
		if (explicitProductNumericId) {
			return explicitProductNumericId;
		}
		if (prepNumericId) {
			return prepNumericId;
		}
		switch (productType) {
			case "STUDY_PACK":
				return studyPackProductNumericId;
			case "PRACTICE_TEST":
				return practiceTestProductNumericId;
			default:
				return undefined;
		}
	};
	const tenantId = useAppStore((state) => state.tenant.id);
	const numericId = getProductNumericId();

	return useQuery({
		queryKey: [cacheKeys.productDetails, numericId, tenantId],
		queryFn: async () => {
			return await fetchProductDetails({
				numericId: numericId!,
			});
		},
		enabled:
			Boolean(tenantId) &&
			Boolean(numericId) &&
			Boolean(isSignedIn) &&
			(options?.enabled ?? true) &&
			!isExamContextLoading,
	});
};

export const useProductMetadata = (
	productType: ProductType,
	explicitProductId?: string,
) => {
	const {
		currentExamContext: { studyPackProductId, practiceTestProductId, prepId },
		isExamContextLoading,
	} = useExamContext();

	const { isSignedIn } = useAuth();

	const getProductId = () => {
		if (explicitProductId) {
			return explicitProductId;
		}
		if (prepId) {
			return prepId;
		}
		switch (productType) {
			case "STUDY_PACK":
				return studyPackProductId;
			case "PRACTICE_TEST":
				return practiceTestProductId;
			default:
				return undefined;
		}
	};
	const { userLogicalId } = useAuthContext();
	const productId = isExamContextLoading ? undefined : getProductId();

	const query = useQuery({
		queryKey: [cacheKeys.productMetadata, productId],
		queryFn: async () => {
			const result = await fetchProductMetadata({
				productId: productId!,
			});
			return result;
		},
		staleTime: PRODUCT_METADATA_STALE_TIME,
		enabled:
			!!userLogicalId && !!productId && isSignedIn && !isExamContextLoading,
	});

	return { query, productId };
};
